import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { patchOrcamentoHtmlMedia } from '@/utils/orcamentoUtils'

async function fetchImageAsDataUrl (url) {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (!res.ok) return null
    const blob = await res.blob()
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

async function inlineImagesForPdf (root) {
  const imgs = root.querySelectorAll('img')
  await Promise.all([...imgs].map(async (img) => {
    const src = img.getAttribute('src')
    if (!src || src.startsWith('data:')) return
    const dataUrl = await fetchImageAsDataUrl(src)
    if (dataUrl) img.src = dataUrl
  }))
}

async function waitForImages (root) {
  const imgs = root.querySelectorAll('img')
  if (!imgs.length) return
  await Promise.all([...imgs].map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve()
    return new Promise((resolve) => {
      img.onload = () => resolve()
      img.onerror = () => resolve()
      setTimeout(resolve, 10000)
    })
  }))
}

async function buildPdfFromElement (element) {
  await waitForImages(element)
  await inlineImagesForPdf(element)

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#ffffff',
    logging: false
  })
  const imgData = canvas.toDataURL('image/png')
  const pdf = new jsPDF('p', 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const imgWidth = pageWidth
  const imgHeight = (canvas.height * imgWidth) / canvas.width
  let heightLeft = imgHeight
  let position = 0

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
  heightLeft -= pageHeight

  while (heightLeft > 0) {
    position = heightLeft - imgHeight
    pdf.addPage()
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight
  }

  return pdf
}

async function loadHtmlInIframe (html, fotoWorkspaceFallback = '') {
  const patched = patchOrcamentoHtmlMedia(html, fotoWorkspaceFallback)
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.left = '-9999px'
  iframe.style.top = '0'
  iframe.style.width = '820px'
  iframe.style.border = 'none'
  document.body.appendChild(iframe)

  await new Promise((resolve, reject) => {
    iframe.onload = resolve
    iframe.onerror = reject
    iframe.srcdoc = patched
  })

  const doc = iframe.contentDocument
  const target = doc?.querySelector('.doc') || doc?.body
  return { iframe, target }
}

export async function exportOrcamentoPdf (element, filename = 'orcamento.pdf') {
  if (!element) return
  const pdf = await buildPdfFromElement(element)
  pdf.save(filename)
}

export async function exportOrcamentoPdfFromHtml (html, filename = 'orcamento.pdf', fotoWorkspaceFallback = '') {
  return exportOrcamentoPdfFromFullHtml(html, filename, fotoWorkspaceFallback)
}

export async function exportOrcamentoPdfFromFullHtml (html, filename = 'orcamento.pdf', fotoWorkspaceFallback = '') {
  if (!html) return

  const { iframe, target } = await loadHtmlInIframe(html, fotoWorkspaceFallback)
  try {
    if (!target) return
    await exportOrcamentoPdf(target, filename)
  } finally {
    document.body.removeChild(iframe)
  }
}

/** Gera arquivo PDF em memória (ex.: envio WhatsApp). */
export async function generateOrcamentoPdfFile (html, filename = 'orcamento.pdf', fotoWorkspaceFallback = '') {
  if (!html) return null

  const { iframe, target } = await loadHtmlInIframe(html, fotoWorkspaceFallback)
  try {
    if (!target) return null
    const pdf = await buildPdfFromElement(target)
    const blob = pdf.output('blob')
    const safeName = filename.endsWith('.pdf') ? filename : `${filename}.pdf`
    return new File([blob], safeName, { type: 'application/pdf' })
  } finally {
    document.body.removeChild(iframe)
  }
}
